import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import HeroV1 from '../components/HeroV1.jsx';
import ToolStack from '../components/ToolStack.jsx';
import ThreeQuestionsB from '../components/ThreeQuestions.jsx';
import { LeadFormBand } from '../components/LeadForm.jsx';
import CTABand from '../components/CTABand.jsx';
import SEO from '../components/SEO.jsx';
import { ArrowL, WhatsAppGlyph } from '../components/Icons.jsx';
import useReveal from '../hooks/useReveal.js';
import { WA_URL } from '../config.js';
import { localBusinessSchema } from '../data/schemas.js';

// ─────────────────────────────────────────────────────────────
// MIRROR. "אם זה נשמע מוכר"
// ─────────────────────────────────────────────────────────────
function HomeMirror() {
  const quotes = [
    { t: 'אנחנו כבר לא יכולים להמשיך לעבוד ככה.', pip: 'coral' },
    { t: 'יש לנו עסק טוב.\nאבל זה לא נראה ככה בפועל.', pip: 'yellow' },
    { t: 'העסק גדל.\nאבל התהליכים של העסק לא מותאמים.', pip: 'lilac' },
    { t: 'הכל תקוע לנו.\nכל דבר קטן נהיה סיפור.', pip: 'teal' },
    { t: 'תהליכים ידניים ארוכים מפוזרים ברחבי הארגון.', pip: 'coral' },
    { t: 'אנחנו עובדים קשה מדי, בשביל תוצאה בינונית.', pip: 'yellow' },
  ];
  return (
    <section className="hm-mirror">
      <div className="wrap">
        <div className="hm-mirror-head reveal">
          <div className="hm-eyebrow"><span className="hm-eyebrow-bar"></span>אם זה נשמע מוכר</div>
          <h2 className="hm-h2">
            את <span className="hl-block tilt-l">המשפטים האלה</span><br />
            FocusMode שומעת כל הזמן.
          </h2>
          <p className="hm-lede pre">
            {'לקוחות מגיעים עם משפטים שונים.\nאנחנו רוצים אתר, מערכת, אוטומציה, AI.\nמתחת למשפטים אלו לרוב יושב אותו הדבר :\nקובעים לעצמם התהליך או את מה שהם צריכים עולים לאוויר ואז מגלים ברגעי האמת שזה לא זה.'}
          </p>
        </div>

        <div className="hm-mirror-grid">
          {quotes.map((q, i) => (
            <figure key={i} className="hm-quote reveal pre" data-pip={q.pip}>
              <span className="hm-quote-mark" aria-hidden="true">"</span>
              <blockquote>{q.t}</blockquote>
              <span className="hm-quote-pip" aria-hidden="true"></span>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// SERVICES. pain-first framing per service.
// ─────────────────────────────────────────────────────────────
function HomeServices() {
  const items = [
    {
      num: '01',
      slug: 'web',
      title: 'בניית אתרים',
      pain: '"האתר שלנו לא נראה כמו הרמה של העסק."',
      what: 'אתרי תדמית, דפי נחיתה, חנויות אונליין ומערכות ווב (WEB).\nתכנון, עיצוב ופיתוח במקום אחד.',
      fit: 'בנייה מאפס או עיצוב מחדש.',
      accent: 'lilac',
    },
    {
      num: '02',
      slug: 'landing-pages',
      title: 'דפי נחיתה',
      pain: '"צריכים להעלות קמפיין מחר, ולא יודעים מאיפה מתחילים."',
      what: 'דף נחיתה ממוקד.\nבנוי למטרה אחת ברורה:\nפניות, הרשמות או מכירות.',
      fit: 'קמפיין ממומן, השקה, מוצר חדש.',
      accent: 'coral',
    },
    {
      num: '03',
      slug: 'systems',
      title: 'מערכות ואינטגרציות',
      pain: '"כל דבר עובר ידנית. אנחנו עושים מיליון העתק-הדבק."',
      what: 'יש לכם כמה מערכות שלא מתקשרות זו עם זו?\nמחברים כלים, מסנכרנים מידע\nומייעלים תהליכי עבודה.',
      fit: 'עסקים וארגונים עם מערכות קיימות שצריכות לעבוד יחד.',
      accent: 'teal',
    },
    {
      num: '04',
      slug: 'automations',
      title: 'אוטומציות',
      pain: '"אני עושה את אותו דבר ידנית כל יום."',
      what: 'תהליך שחוזר על עצמו שוב ושוב?\nבונים אותו פעם אחת ונותנים לו לעבוד אוטומטית.',
      fit: 'כל עסק או ארגון עם תהליכים שחוזרים על עצמם.',
      accent: 'yellow',
    },
  ];

  return (
    <section className="hm-services" id="services">
      <div className="wrap">
        <div className="hm-services-head reveal">
          <div className="hm-eyebrow"><span className="hm-eyebrow-bar"></span>השירותים</div>
          <h2 className="hm-h2">
            לא בטוחים מה אתם צריכים?<br />
            בואו נתחיל מ<span className="hl-block tilt-r">נקודות הכאב</span>.
          </h2>
          <p className="hm-lede pre">
            {'כל שירות פותר כאב אחר.\nאם לא ברור לאן זה משתייך, תכתבו מה תקוע.\nFocusMode תגיד מה באמת נכון לכם.'}
          </p>
        </div>

        <div className="hm-svc-list">
          {items.map(s => (
            <Link key={s.slug} className="hm-svc-row reveal" to={`/services/${s.slug}`} data-accent={s.accent}>
              <div className="hm-svc-num">
                {s.num}
                <span className="hm-svc-pip" aria-hidden="true"></span>
              </div>
              <div className="hm-svc-body">
                <div className="hm-svc-pain pre">{s.pain}</div>
                <h3 className="hm-svc-title">{s.title}</h3>
                <p className="hm-svc-what pre">{s.what}</p>
                <div className="hm-svc-fit">
                  <span className="hm-svc-fit-label">מתאים ל</span>
                  <span className="pre">{s.fit}</span>
                </div>
              </div>
              <div className="hm-svc-arrow" aria-hidden="true">
                <ArrowL size={20} />
              </div>
            </Link>
          ))}
        </div>

        <a className="hm-svc-other reveal" href={WA_URL} target="_blank" rel="noopener noreferrer">
          <div>
            <h4>לא ברור לכם לאיזה סוג זה משתייך?</h4>
            <p className="pre">{'תכתבו 3 משפטים על הבעיה שלכם.\nFocusMode תגיד אם זה אחד מהשירותים או משהו אחר לגמרי.'}</p>
          </div>
          <span className="hm-svc-other-cta">
            <WhatsAppGlyph size={18} />
            לוואטסאפ
            <ArrowL size={16} />
          </span>
        </a>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// HOW FOCUSMODE IS DIFFERENT
// ─────────────────────────────────────────────────────────────
function HomeDifferent() {
  const cells = [
    {
      n: '01',
      title: 'תמונה מלאה.\nלא רק משימה.',
      vs: 'פרילנסר מבצע משימה ספציפית.',
      me: 'פוקוס מוד מבינה למה הבעיה קיימת.\nשיווק, תפעול, עובדים.\nלא רק סיום המשימה ובקשת הלקוח.',
    },
    {
      n: '02',
      title: 'יד אחת.\nלא פס ייצור.',
      vs: 'סוכנות מפזרת בין 4 עובדים שונים.',
      me: 'מנהל הפרויקט הוא גם מי שמבצע בפועל.\nאין מנהל לקוח שמעביר הודעות לצוות שעובד מאחורי הקלעים.',
    },
    {
      n: '03',
      title: 'נשארת איתכם גם אחרי העלייה לאוויר.',
      vs: 'ספק מעביר קבצים ונעלם.',
      me: 'ליווי כלול לאחר המסירה.\nפרויקט נמדד באיך שהוא עובד לאורך זמן.\nלא רק איך שהוא נמסר.',
    },
    {
      n: '04',
      title: 'אומרת "לא" כשצריך.',
      vs: 'ספקים אומרים "כן".\nכדי לסגור.',
      me: 'אם השירות לא מתאים.\nFocusMode אומרת את זה מראש.\nעדיף לשמוע את זה מאיתנו.',
    },
  ];
  return (
    <section className="hm-diff">
      <div className="wrap">
        <div className="hm-diff-head reveal">
          <div className="hm-eyebrow hm-eyebrow-light"><span className="hm-eyebrow-bar"></span>למה פוקוס מוד</div>
          <h2 className="hm-h2 hm-h2-light">
            לא <span className="hl-block tilt-l">סוכנות</span>.<br />
            לא פרילנסר.
          </h2>
          <p className="hm-lede hm-lede-light pre">
            {'אלה הדברים שמנחים כל עבודה ב-FocusMode.\nלא סיסמאות ולא הבטחות.\nדרך עבודה.'}
          </p>
        </div>

        <div className="hm-diff-grid">
          {cells.map(c => (
            <article key={c.n} className="hm-diff-card reveal">
              <div className="hm-diff-num">{c.n}</div>
              <h3 className="hm-diff-title pre">{c.title}</h3>
              <div className="hm-diff-vs">
                <span className="hm-diff-vs-label">אחרים</span>
                <p className="pre">{c.vs}</p>
              </div>
              <div className="hm-diff-me">
                <span className="hm-diff-me-label">פוקוס מוד</span>
                <p className="pre">{c.me}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// PROCESS. 4 steps.
// ─────────────────────────────────────────────────────────────
function HomeProcess() {
  const steps = [
    {
      n: '01',
      title: 'שיחת היכרות',
      lines: ['FocusMode שואלת מה העסק עושה.', 'מה כואב.', 'מה כבר נוסה בעבר.', 'בסוף השיחה ברור אם זה מתאים.'],
    },
    {
      n: '02',
      title: 'שיקוף והצעה',
      lines: ['מסמך אחד.', 'מה הבנו.', 'מה מומלץ.', 'מה זה כולל.', 'בלי הפתעות.', 'בלי הבטחות.'],
    },
    {
      n: '03',
      title: 'בנייה',
      lines: ['עדכון סטטוס שבועי.', 'לא הודעות מפוזרות.', 'שינויים בתוך התכנית.'],
    },
    {
      n: '04',
      title: 'מסירה וליווי',
      lines: ['תיעוד.', 'הדרכה.', 'תקופת ליווי כלולה.', 'המשך עבודה רק אם יש בו ערך אמיתי.'],
    },
  ];
  return (
    <section className="hm-process" id="process">
      <div className="wrap">
        <div className="hm-process-head reveal">
          <div className="hm-eyebrow"><span className="hm-eyebrow-bar"></span>איך זה עובד</div>
          <h2 className="hm-h2">
            תהליך <span className="hl-block tilt-r">ברור</span>.<br />
            בלי הפתעות.
          </h2>
        </div>

        <div className="hm-process-list">
          {steps.map((s) => (
            <details key={s.n} className="hm-process-acc reveal">
              <summary className="hm-process-acc-head">
                <span className="hm-process-n">{s.n}</span>
                <span className="hm-process-title">{s.title}</span>
                <span className="hm-process-acc-arrow" aria-hidden="true">↓</span>
              </summary>
              <ul className="hm-process-acc-body">
                {s.lines.map((l, i) => (<li key={i}>{l}</li>))}
              </ul>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// FEATURED. FlyMoments trust story.
// ─────────────────────────────────────────────────────────────
function HomeFeatured() {
  return (
    <section className="hm-feat">
      <div className="wrap">
        <div className="hm-feat-card reveal">
          <div className="hm-feat-side">
            <div className="hm-feat-eyebrow">לקוח · סיפור אמיתי</div>
            <h3 className="hm-feat-title">FlyMoments</h3>
            <p className="hm-feat-kicker">חברת חוויות MICE.<br />כנסים, אירועים תאגידיים, אינסנטיב.</p>

            <div className="hm-feat-timeline">
              <div className="hm-feat-row">
                <div className="hm-feat-year">2017</div>
                <div className="hm-feat-text pre">{'היכרות בעולם ההפקות.\nפרויקט ראשון.'}</div>
              </div>
              <div className="hm-feat-row">
                <div className="hm-feat-year">2018</div>
                <div className="hm-feat-text pre">{'חזרו לעבוד.\nפרויקט שני, גדול יותר.'}</div>
              </div>
              <div className="hm-feat-row hm-feat-row-now">
                <div className="hm-feat-year">2026</div>
                <div className="hm-feat-text pre">{'כשהיה צריך אתר חדש.\nהייתה רק שיחה אחת.'}</div>
              </div>
            </div>

            <p className="hm-feat-quote">
              <span aria-hidden="true">"</span>הוא עומד במה שהוא מבטיח<span aria-hidden="true">"</span>
            </p>
            <p className="hm-feat-quote-by pre">{'הסיבה שהם חוזרים.\nשמונה שנים.'}</p>

            <a className="hm-feat-cta" href="https://flymoments.com" target="_blank" rel="noopener noreferrer">
              flymoments.com <ArrowL size={16} />
            </a>
          </div>

          <div className="hm-feat-visual" aria-hidden="true">
            <div className="fm-mac">
              <div className="fm-mac-bezel">
                <div className="fm-mac-chrome">
                  <div className="fm-mac-lights"><span></span><span></span><span></span></div>
                  <div className="fm-mac-url">
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                      <path d="M12 2a15.3 15.3 0 0 0 0 20" />
                    </svg>
                    flymoments.com
                  </div>
                  <div className="fm-mac-camera"></div>
                </div>
                <div className="fm-mac-screen">
                  <div className="fm-mac-hero">
                    <div className="fm-mac-eyebrow">MICE · Strategic B2B travel</div>
                    <div className="fm-mac-h1">Travel that <em>moves</em><br />your business <em>forward.</em></div>
                    <div className="fm-mac-cta">LET'S CONNECT →</div>
                  </div>
                  <div className="fm-mac-img" aria-hidden="true">
                    <div className="fm-mac-img-sun"></div>
                    <div className="fm-mac-img-tag">flymoments.com</div>
                  </div>
                </div>
              </div>
              <div className="fm-mac-notch"></div>
              <div className="fm-mac-base"></div>
            </div>
            <div className="fm-mac-caption">
              <span className="fm-mac-caption-dot"></span>
              Live · אפריל 2026
            </div>
          </div>
        </div>

        <div className="hm-feat-foot reveal pre">
          <span className="hm-feat-foot-mark" aria-hidden="true"></span>
          {'12 שנה בדיגיטל.\nעבודה עם סוכנויות, מותגים ועמותות בישראל.'}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// PAGE COMPOSITION
// ─────────────────────────────────────────────────────────────
export default function Home() {
  useReveal();
  return (
    <main className="home-v2">
      <SEO
        title="FocusMode | סדר. מערכת. ראש שקט."
        description="הובלת פרויקטים, בניית אתרים, מערכות ואוטומציות. נכנסת לארגונים שנמצאים בכאוס תפעולי או צמיחה מהירה ובונה תשתית עבודה שמחזיקה."
        path="/"
        schema={[localBusinessSchema]}
      />
      <HeroV1 />
      <HomeMirror />
      <HomeServices />
      <HomeDifferent />
      <HomeProcess />
      {/* <HomeFeatured /> */}{/* TODO: להחזיר אחרי אישור FlyMoments */}
      {/* <ToolStack /> */}
      <ThreeQuestionsB />
      <LeadFormBand
        title={<>3 משפטים, <span className="hl-block tilt-l">תשובה אמיתית</span>.</>}
        body={'לא ברור אם זה מתאים? כתבו בכל זאת.'}
      />
      <CTABand
        title={<>יש פרויקט <span className="hl-block tilt-r">שתקוע</span>?</>}
        body={'3 משפטים בוואטסאפ.\nפוקוס מוד חוזרת תוך 48 שעות.'}
        cta="לוואטסאפ"
        source="cta-band-home"
      />
    </main>
  );
}
